/**
 * The conversation and the machine behind its newest message.
 *
 * One rule carries the render performance budget: machine state lands in
 * React at most once per animation frame. The machine publishes on every
 * frame it applies; the hook keeps only the latest and flushes it on the
 * next `requestAnimationFrame`, so a burst of five thousand tokens produces
 * at most sixty renders a second, not five thousand.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createInitialState,
  createStreamMachine,
  isTerminalPhase,
  type StreamMachine,
  type StreamState,
} from '@harness/stream-core';
import { createRandom } from '@harness/protocol';

import { browserTimers, now, random } from '../lib/environment.ts';
import { createFetchTransport } from '../transport/fetch-sse.ts';
import {
  DEFAULT_SETTINGS,
  buildOverrides,
  type HarnessSettings,
  type Knob,
} from './use-settings.ts';

export interface ScenarioInfo {
  id: string;
  title: string;
  proves: string;
  knobs: Knob[];
}

export interface UserTurn {
  kind: 'user';
  id: number;
  text: string;
}

export interface AssistantTurn {
  kind: 'assistant';
  id: number;
  state: StreamState;
}

export type Turn = UserTurn | AssistantTurn;

export interface ChatController {
  turns: Turn[];
  scenarios: ScenarioInfo[];
  scenario: string;
  setScenario: (id: string) => void;
  /** True while a stream is live, queued, thinking, or reconnecting. */
  busy: boolean;
  /** The machine state behind the newest assistant turn, if any. */
  liveState: StreamState | null;
  /** The newest assistant turn's id: the one whose machine still exists. */
  liveTurnId: number | null;
  send: (message: string) => void;
  cancel: () => void;
  retry: () => void;
  /** Cancels any live stream and drops every turn, back to a fresh idle machine. */
  clear: () => void;
}

export function useChat(
  settings: HarnessSettings = DEFAULT_SETTINGS,
  scenarios: ScenarioInfo[] = [],
): ChatController {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [scenario, setScenario] = useState('chaos');
  const [busy, setBusy] = useState(false);

  const machineRef = useRef<StreamMachine | null>(null);
  const activeTurnRef = useRef<number | null>(null);
  const pendingStateRef = useRef<StreamState | null>(null);
  const frameRef = useRef<number | null>(null);
  const nextIdRef = useRef(1);

  const applyState = useCallback((turnId: number, state: StreamState) => {
    setTurns((current) =>
      current.map((turn) =>
        turn.kind === 'assistant' && turn.id === turnId ? { ...turn, state } : turn,
      ),
    );
  }, []);

  /** Batch: keep only the newest state, flush once per animation frame. */
  const scheduleFlush = useCallback(
    (turnId: number) => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        const pending = pendingStateRef.current;
        if (pending !== null) applyState(turnId, pending);
      });
    },
    [applyState],
  );

  const send = useCallback(
    (message: string) => {
      if (machineRef.current !== null && !isTerminalPhase(machineRef.current.state.phase)) return;
      const text = message.trim();
      if (text === '') return;

      const userId = nextIdRef.current;
      const assistantId = nextIdRef.current + 1;
      nextIdRef.current += 2;
      activeTurnRef.current = assistantId;

      const scenarioInfo = scenarios.find((s) => s.id === scenario);
      const overrides = buildOverrides(settings, scenario, scenarioInfo?.knobs ?? []);

      const machine = createStreamMachine({
        transport: createFetchTransport(),
        streamUrl: '/v1/chat/stream',
        resumeUrl: '/v1/chat/resume',
        body: { conversationId: 'demo', message: text, scenario, overrides },
        timers: browserTimers,
        now,
        random: settings.client.seededJitter ? createRandom(settings.randomizer.seed) : random,
        maxReconnectAttempts: settings.client.maxReconnectAttempts,
        byteTimeoutMs: settings.client.byteTimeoutMs,
        contentTimeoutMs: settings.client.contentTimeoutMs,
        onChange: (state) => {
          pendingStateRef.current = state;
          scheduleFlush(assistantId);
        },
      });
      machineRef.current = machine;

      setTurns((current) => [
        ...current,
        { kind: 'user', id: userId, text },
        { kind: 'assistant', id: assistantId, state: createInitialState() },
      ]);
      setBusy(true);

      machine
        .start()
        .catch(() => machine.state)
        .then((finalState) => {
          // The terminal state always lands, even if the tab was hidden and
          // the animation frame never fired.
          applyState(assistantId, finalState);
          setBusy(false);
        })
        .catch(() => {
          setBusy(false);
        });
    },
    [applyState, scenario, scheduleFlush, settings, scenarios],
  );

  const cancel = useCallback(() => {
    machineRef.current?.cancel();
  }, []);

  const retry = useCallback(() => {
    const machine = machineRef.current;
    const turnId = activeTurnRef.current;
    if (machine === null || turnId === null) return;
    setBusy(true);
    machine
      .retry()
      .catch(() => machine.state)
      .then((finalState) => {
        applyState(turnId, finalState);
        setBusy(false);
      })
      .catch(() => {
        setBusy(false);
      });
  }, [applyState]);

  const clear = useCallback(() => {
    machineRef.current?.cancel();
    machineRef.current = null;
    activeTurnRef.current = null;
    pendingStateRef.current = null;
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    setTurns([]);
    setBusy(false);
  }, []);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const liveTurn = [...turns].reverse().find((turn) => turn.kind === 'assistant');

  return {
    turns,
    scenarios,
    scenario,
    setScenario,
    busy,
    liveState: liveTurn?.kind === 'assistant' ? liveTurn.state : null,
    liveTurnId: liveTurn?.id ?? null,
    send,
    cancel,
    retry,
    clear,
  };
}
